import { ReactNode } from "react";
import PhLightBellSvg from "../assets/svg-fc/PhLightBellSvg";
import PhLightCalendarCheckSvg from "../assets/svg-fc/PhLightCalendarCheckSvg";
import PhLightCalendarPlusSvg from "../assets/svg-fc/PhLightCalendarPlusSvg";
import PhLightCalendarXSvg from "../assets/svg-fc/PhLightCalendarXSvg";
import PhLightChatCircleTextSvg from "../assets/svg-fc/PhLightChatCircleTextSvg";
import PhLightClockCountdownSvg from "../assets/svg-fc/PhLightClockCountdownSvg";
import PhLightPencilSimpleLineSvg from "../assets/svg-fc/PhLightPencilSimpleLineSvg";
import PhLightReceiptSvg from "../assets/svg-fc/PhLightReceiptSvg";
import PhLightXCircleSvg from "../assets/svg-fc/PhLightXCircleSvg";

export enum NOTIFICATION_TYPES {
	NEW_MESSAGE = "NEW_MESSAGE",
	CONGRATULATION = "CONGRATULATION",
	EVENT_DECLINE = "CUSTOMER_DECLINED_QUOTE",
	EVENT_CANCELLED = "EVENT_CANCELLED",
	NEW_DETAIL_AVAILABLE = "CUSTOMER_CREATED_NEW_EVENT",
	EVENT_REQUEST = "CUSTOMER_REQUEST_TO_QUOTE",
	CUSTOMER_ACCEPTED_QUOTE = "CUSTOMER_ACCEPTED_QUOTE",
	EVENT_PENDING_IN_72_HOURS = "EVENT_PENDING_IN_72_HOURS",
	CORPORATE_EVENT_REQUEST = "CORPORATE_EVENT_REQUEST",
	CORPORATE_EVENT_BID_ACCEPTED = "CORPORATE_EVENT_BID_ACCEPTED",
	CORPORATE_EVENT_BID_DECLINED = "CORPORATE_EVENT_BID_DECLINED",
	CORPORATE_EVENT_CANCELLED = "CORPORATE_EVENT_CANCELLED",
}

export enum NOTIFICATION_CATEGORIES {
	MESSAGES = "messages",
	NEW_REQUESTS = "new_requests",
	QUOTES_BIDS = "quotes_bids",
	CONFIRMED = "confirmed",
	DECLINED_CANCELLED = "declined_cancelled",
	REMINDERS = "reminders",
	EVENT_CHANGES = "event_changes",
	OTHER = "other",
}

export const NOTIFICATION_CATEGORY_ORDER = [
	NOTIFICATION_CATEGORIES.MESSAGES,
	NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	NOTIFICATION_CATEGORIES.QUOTES_BIDS,
	NOTIFICATION_CATEGORIES.CONFIRMED,
	NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	NOTIFICATION_CATEGORIES.REMINDERS,
	NOTIFICATION_CATEGORIES.EVENT_CHANGES,
	NOTIFICATION_CATEGORIES.OTHER,
];

interface iNotificationCategoryStyle {
	label: string;
	tag: string;
	icon: string;
	text: string;
	tint: string;
}

export const NOTIFICATION_CATEGORY_STYLES: Record<NOTIFICATION_CATEGORIES, iNotificationCategoryStyle> = {
	[NOTIFICATION_CATEGORIES.MESSAGES]: {
		label: "Messages",
		tag: "Message",
		icon: "#247BA0",
		text: "#247BA0",
		tint: "#E9F2F6",
	},
	[NOTIFICATION_CATEGORIES.NEW_REQUESTS]: {
		label: "New requests",
		tag: "New request",
		icon: "#22031F",
		text: "#22031F",
		tint: "#F8F1E1",
	},
	[NOTIFICATION_CATEGORIES.QUOTES_BIDS]: {
		label: "Quotes & bids",
		tag: "Quote",
		icon: "#6B21A8",
		text: "#6B21A8",
		tint: "#F3E8FF",
	},
	[NOTIFICATION_CATEGORIES.CONFIRMED]: {
		label: "Confirmed",
		tag: "Confirmed",
		icon: "#789B73",
		text: "#5A7555",
		tint: "#F2F5F1",
	},
	[NOTIFICATION_CATEGORIES.DECLINED_CANCELLED]: {
		label: "Declined & cancelled",
		tag: "Declined",
		icon: "#B42318",
		text: "#B42318",
		tint: "#FEE2E2",
	},
	[NOTIFICATION_CATEGORIES.REMINDERS]: {
		label: "Reminders",
		tag: "Reminder",
		icon: "#E79939",
		text: "#8A5B12",
		tint: "#FCF3E7",
	},
	[NOTIFICATION_CATEGORIES.EVENT_CHANGES]: {
		label: "Event changes",
		tag: "Event change",
		icon: "#0F766E",
		text: "#0F766E",
		tint: "#E7F4F2",
	},
	[NOTIFICATION_CATEGORIES.OTHER]: {
		label: "Other",
		tag: "Update",
		icon: "#66707A",
		text: "#66707A",
		tint: "#F3F4F6",
	},
};

const CATEGORY_BY_TYPE: Record<string, NOTIFICATION_CATEGORIES> = {
	NEW_MESSAGE: NOTIFICATION_CATEGORIES.MESSAGES,
	NEW_MENTION: NOTIFICATION_CATEGORIES.MESSAGES,
	NEW_ADMIN_MESSAGE: NOTIFICATION_CATEGORIES.MESSAGES,
	ADMIN_VENDOR_SUPPORT_CHAT: NOTIFICATION_CATEGORIES.MESSAGES,
	NEW_MENTION_SUPPORT_CHAT: NOTIFICATION_CATEGORIES.MESSAGES,
	CORPORATE_EVENT_REQUEST: NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	CUSTOMER_REQUEST_TO_QUOTE: NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	CUSTOMER_CREATED_NEW_EVENT: NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	AI_DIRECT_REQUEST: NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	EVENT_REQUEST_CREATED_NOTIFICATION: NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	PREPAID_EVENT_REQUEST_CREATED: NOTIFICATION_CATEGORIES.NEW_REQUESTS,
	ADMIN_SUBMITTED_QUOTE_FOR_VENDOR: NOTIFICATION_CATEGORIES.QUOTES_BIDS,
	VENDOR_ADDED_THE_QUOTE_TO_EVENT: NOTIFICATION_CATEGORIES.QUOTES_BIDS,
	CORPORATE_EVENT_BID_CREATED: NOTIFICATION_CATEGORIES.QUOTES_BIDS,
	CUSTOMER_ACCEPTED_QUOTE: NOTIFICATION_CATEGORIES.CONFIRMED,
	CORPORATE_EVENT_BID_ACCEPTED: NOTIFICATION_CATEGORIES.CONFIRMED,
	CONGRATULATION: NOTIFICATION_CATEGORIES.CONFIRMED,
	CUSTOMER_DECLINED_QUOTE: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	EVENT_CANCELLED: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	CORPORATE_EVENT_CANCELLED: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	CORPORATE_EVENT_BID_DECLINED: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	CORPORATE_EVENT_DECLINED_FOR_VENDOR: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	EVENT_REQUEST_CANCELLED_NOTIFICATION: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	EVENT_REQUEST_CANCELLED_NOTIFICATION_BY_ADMIN: NOTIFICATION_CATEGORIES.DECLINED_CANCELLED,
	EVENT_PENDING_IN_72_HOURS: NOTIFICATION_CATEGORIES.REMINDERS,
	EVENT_DETAILS_UPDATED: NOTIFICATION_CATEGORIES.EVENT_CHANGES,
	CORPORATE_EVENT_DETAILS_UPDATED: NOTIFICATION_CATEGORIES.EVENT_CHANGES,
	EVENT_REQUEST_UPDATED_NOTIFICATION: NOTIFICATION_CATEGORIES.EVENT_CHANGES,
	EVENT_REQUEST_ON_HOLD_NOTIFICATION: NOTIFICATION_CATEGORIES.EVENT_CHANGES,
};

const TAG_BY_TYPE: Record<string, string> = {
	CORPORATE_EVENT_REQUEST: "New RFP",
	CUSTOMER_ACCEPTED_QUOTE: "Quote accepted",
	CORPORATE_EVENT_BID_ACCEPTED: "Bid accepted",
	VENDOR_ADDED_THE_QUOTE_TO_EVENT: "New quote",
	ADMIN_SUBMITTED_QUOTE_FOR_VENDOR: "New quote",
	CORPORATE_EVENT_BID_CREATED: "New bid",
	EVENT_PENDING_IN_72_HOURS: "In 72 hours",
	CUSTOMER_DECLINED_QUOTE: "Quote declined",
	CORPORATE_EVENT_BID_DECLINED: "Bid declined",
	EVENT_CANCELLED: "Cancelled",
	CORPORATE_EVENT_CANCELLED: "Cancelled",
	EVENT_REQUEST_CANCELLED_NOTIFICATION: "Cancelled",
	EVENT_REQUEST_CANCELLED_NOTIFICATION_BY_ADMIN: "Cancelled",
	EVENT_REQUEST_ON_HOLD_NOTIFICATION: "On hold",
};

const CANCELLED_TYPES = new Set([
	"EVENT_CANCELLED",
	"CORPORATE_EVENT_CANCELLED",
	"EVENT_REQUEST_CANCELLED_NOTIFICATION",
	"EVENT_REQUEST_CANCELLED_NOTIFICATION_BY_ADMIN",
]);

const isNotificationCategory = (value?: string | null): value is NOTIFICATION_CATEGORIES =>
	!!value && value in NOTIFICATION_CATEGORY_STYLES;

export const notificationCategoryOf = ({
	category,
	type,
}: {
	category?: string | null;
	type?: string | null;
}): NOTIFICATION_CATEGORIES => {
	if (isNotificationCategory(category)) return category;
	return (type && CATEGORY_BY_TYPE[type]) || NOTIFICATION_CATEGORIES.OTHER;
};

export const notificationTagOf = (category: NOTIFICATION_CATEGORIES, type?: string | null) =>
	(type && TAG_BY_TYPE[type]) || NOTIFICATION_CATEGORY_STYLES[category].tag;

export const notificationTypeSVG = (
	category: NOTIFICATION_CATEGORIES,
	type?: string | null,
	size = 16,
): ReactNode => {
	switch (category) {
		case NOTIFICATION_CATEGORIES.MESSAGES:
			return <PhLightChatCircleTextSvg size={size} />;
		case NOTIFICATION_CATEGORIES.NEW_REQUESTS:
			return <PhLightCalendarPlusSvg size={size} />;
		case NOTIFICATION_CATEGORIES.QUOTES_BIDS:
			return <PhLightReceiptSvg size={size} />;
		case NOTIFICATION_CATEGORIES.CONFIRMED:
			return <PhLightCalendarCheckSvg size={size} />;
		case NOTIFICATION_CATEGORIES.DECLINED_CANCELLED:
			return type && CANCELLED_TYPES.has(type) ? (
				<PhLightCalendarXSvg size={size} />
			) : (
				<PhLightXCircleSvg size={size} />
			);
		case NOTIFICATION_CATEGORIES.REMINDERS:
			return <PhLightClockCountdownSvg size={size} />;
		case NOTIFICATION_CATEGORIES.EVENT_CHANGES:
			return <PhLightPencilSimpleLineSvg size={size} />;
		default:
			return <PhLightBellSvg size={size} />;
	}
};
